import merge from 'lodash/merge';
import { createTheme, responsiveFontSizes } from '@mui/material/styles';
import type { Direction, Theme, ThemeOptions } from '@mui/material';
import { THEMES } from '../constants';
import { lightShadows } from './shadows';
import './partials/variables.css';
import './partials/darkModePalettes.css';
import './partials/CustomerSelectPopover.css';
import './partials/DashboardLayoutContent.css';
import './partials/ContentSearch.css';
import './partials/ContentHeader.css';
import './partials/ItemDetails.css';

interface ThemeConfig {
  direction?: Direction;
  responsiveFontSizes?: boolean;
  roundedCorners?: boolean;
  theme?: string;
}

const baseOptions: ThemeOptions = {
  direction: 'ltr',
  spacing: 6,
  components: {
    MuiAvatar: {
      styleOverrides: {
        fallback: {
          height: '75%',
          width: '75%',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '50px',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'none',
          },
          '&.Mui-checked .MuiSvgIcon-root': {
            color: '#3e92cc',
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          borderRadius: '50px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          boxSizing: 'border-box',
        },
        html: {
          MozOsxFontSmoothing: 'grayscale',
          WebkitFontSmoothing: 'antialiased',
          height: '100%',
          width: '100%',
        },
        body: {
          height: '100%',
        },
        '#root': {
          height: '100%',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: '3px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '3px',
          marginBottom: 5,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: {
          variant: 'h6',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '15px',
          paddingTop: '5px',
          paddingBottom: '5px',
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          marginRight: '16px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // borderRadius: 8,
          boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.2), 0px 0px 3px 1px rgba(0, 0, 0, 0.1);',
        },
      },
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"',

    button: {
      fontWeight: 600,
    },
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
    },
    h2: {
      fontWeight: 600,
      fontSize: '3rem',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    overline: {
      fontWeight: 600,
      fontSize: '1em',
      margin: 0,
    },
  },
};

const darkThemeBase: ThemeOptions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 0.86,
            color: '#42526e',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          color: '#000',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: '#000',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { background: '#ccc', color: '#000' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: '#fff',
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        // MuiTab: {},
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#51565c !important',
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&:hover': {
            background: 'none',
          },
          '&.Mui-checked .MuiSvgIcon-root': {
            color: '#fff',
          },
        },
      },
    },
  },
  palette: {
    action: {
      active: '#6b778c',
    },
    background: {
      default: '#2c3430',
      paper: '#48534f',
    },
    error: {
      contrastText: '#ffffff',
      main: '#f44336',
    },
    mode: 'dark',
    primary: {
      contrastText: '#ffffff',
      main: '#51565c',
    },
    success: {
      contrastText: '#ffffff',
      main: '#4caf50',
    },
    text: {
      primary: '#fff',
      secondary: '#ccc',
    },
    warning: {
      contrastText: '#ffffff',
      main: '#ff9800',
    },
  },
  shadows: lightShadows,
};

const themesOptions: Record<string, ThemeOptions> = {
  [THEMES.LIGHT]: {
    components: {
      MuiInputBase: {
        styleOverrides: {
          input: {
            '&::placeholder': {
              opacity: 0.86,
              color: '#42526e',
            },
          },
        },
      },
    },
    palette: {
      action: {
        active: '#6b778c',
      },
      background: {
        default: '#e4e6e9',
        paper: '#ffffff',
      },
      error: {
        contrastText: '#ffffff',
        main: '#f44336',
      },
      mode: 'light',
      primary: {
        contrastText: '#ffffff',
        main: '#042312',
      },
      success: {
        contrastText: '#ffffff',
        main: '#4caf50',
      },
      text: {
        primary: '#172b4d',
        secondary: '#363b44',
      },
      warning: {
        contrastText: '#ffffff',
        main: '#ff9800',
      },
    },
    typography: {
      button: {},
    },
    shadows: lightShadows,
  },
  [THEMES.FOREST]: {
    ...darkThemeBase,
    palette: {
      ...darkThemeBase.palette,
      background: {
        default: '#2c3430',
      },
    },
  },
  [THEMES.NEON]: {
    ...darkThemeBase,
    palette: {
      ...darkThemeBase.palette,
      background: {
        default: '#2c2c34',
      },
    },
  },
  [THEMES.TERMINAL]: {
    ...darkThemeBase,
    palette: {
      ...darkThemeBase.palette,
      background: {
        default: '#000000',
      },
    },
  },
};

export const createCustomTheme = (config: ThemeConfig = {}): Theme => {
  let themeOptions = themesOptions[config.theme];

  if (!themeOptions) {
    // Not using dark theme so I commented out this log.
    // console.warn(new Error(`The theme ${config.theme} is not valid`));
    themeOptions = themesOptions[THEMES.LIGHT];
  }

  let theme = createTheme(
    merge(
      {},
      baseOptions,
      themeOptions,
      {
        ...(config.roundedCorners && {
          shape: {
            borderRadius: 7,
          },
        }),
      },
      {
        direction: config.direction,
      }
    )
  );

  if (config.responsiveFontSizes) {
    theme = responsiveFontSizes(theme);
  }

  return theme;
};
